import React, { useState, useEffect, FC, FormEvent } from 'react'
import t from '../../../translations/main'

import Flex from '../../Layout/Flex/Flex'
import Icon from '../../Core/Iconography/Icon/Icon'
import Font from '../../Core/Typography/Font/Font'
import Button from '../Button/Button'
import Popup from '../Popup/Popup'

import { RequiredMark } from '../Label/Label.styles'
import {
  LabelWrapper,
  InputStyle,
  MainWrapper,
  LabelBlock,
  NameWrapper,
  InputLabelWrapper,
  MaxSizeSpan,
} from './InputFiles.styles'

import { InputFilesProps } from './types'
import { useLocation } from 'react-router-dom'
import { getBaseApiHost } from 'src/components/Core/Utils/url_constants'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import { Trans } from 'react-i18next'

// 1mb = 1024kb and 1kb = 1024 bytes 
const BYTES_IN_ONE_MEGABYTE = 1024 * 1024
const DEFAULT_MAX_FILE_SIZE_50MB = 50

const translationsMessages = {
  loadingFile: t('components.inputFiles.loadingFile'),
  removingFile: t('components.inputFiles.removingFile'),
  fileSizeError: t('components.inputFiles.fileSizeError'),
  fileSize: t('components.inputFiles.fileSize'),
  wrongFileFormatMessage: t('components.inputFiles.wrongFileFormatMessage'),
  // @ts-ignore:next-line
  fileWithVirus: (filename: string) => <Trans i18nKey={'components.inputFiles.fileWithVirus'} values={{ filename }}/>
}

const InputFiles: FC<InputFilesProps> = props => {
  const {
    name,
    label,
    required,
    disabled,
    error,
    serverFileName,
    labelAsTextInput,
    labelAsTextInputPlaceholder,
    labelAsTextInputLabel,
    labelForInputFile,
    fileInputId,
    labelAsTextInputServerValue,
    deleteFile,
    customOnDelete,
    customOnChange,
    removeBorder,
    downloadOnlyFileInfo,
    maxFileSizeMegaBytes = DEFAULT_MAX_FILE_SIZE_50MB,
    deleteRow,
    singleFileInput,
    removeRemoveButton,
    downloadFile,
    accept,
    downloadPreviewClick,
    isDocumentation,
    scanForVirusInfo,
    isEmployee,
  } = props
  const [fileData, setFileData] = useState(null)
  const [eventFileList, setEventFileList] = useState<FormEvent>()
  const [changingFile, setChangingFile] = useState({fileChange: false, changeMessage: ''})
  const [insertNameInfo, setInsertNameInfo] = useState(false)
  const [serverError, setServerError] = useState('')
  const [fileError, setFileError] = useState({error: false, message: ''})
  const { isLoading, sendRequest } = useFetch()
  const { pathname } = useLocation()

  const fileInputIdentifier = `file-input-${label?.replaceAll(' ','') || labelAsTextInputLabel?.replaceAll(' ','')}-${fileInputId}`
  const labelTextId = `labelTextInput-${fileInputId}`

  const checkForVirus = (virusScanUrlValid: string, files: FileList, event?: FormEvent) => {
    const data = new FormData()
    data.append('inputFile', files[0], 'file')
    sendRequest(
      {
        scope: 'onboarding',
        url: `${getBaseApiHost()}/${virusScanUrlValid}`,
        method: 'POST',
        body: data,
        headers: {
          'Accept': 'text/plain',
        },
        noStringfyBody: true
      },
      data => {
        if(!data.cleanResult) {
          // @ts-ignore:next-line
          setFileError({error: true, message: translationsMessages?.fileWithVirus(files?.[0]?.name)})
          setChangingFile({fileChange: false, changeMessage: ''})
          // @ts-ignore:next-line
          event.target.value = ''
        } else {
          setEventFileList(event)
          const filePayload = !labelAsTextInput
            ? { name: files?.[0]?.name || '', data: files?.[0], required: required }
            : { name: files?.[0]?.name || '', data: files?.[0], required: required, inputTextName: (document.getElementById(labelTextId) as HTMLInputElement)?.value }

          props?.onChange?.(
            filePayload,
            data => {
              setFileData({ name: files?.[0]?.name || '', data: files?.[0], documentID: data})
              setChangingFile({fileChange: false, changeMessage: ''})
            },
            downloadOnlyFileInfo?.downloadFileID
          )
        }
      }
    )
  }

  const checkFileProperties = (files: FileList) => {
    if(files.length === 0) return

    if(files?.[0]?.size >= Math.ceil(maxFileSizeMegaBytes * BYTES_IN_ONE_MEGABYTE)) {
      setFileError({error: true, message: translationsMessages.fileSizeError})
      setChangingFile({fileChange: false, changeMessage: ''})
      return
    }

    if(accept.length > 0 && !accept.replaceAll(' ','').split(',').includes(files?.[0].type)){
      setFileError({error: true, message: translationsMessages.wrongFileFormatMessage})
      setChangingFile({fileChange: false, changeMessage: ''})
      return
    }

    setInsertNameInfo(false)
    return true
  }

  const onChange = async (event: FormEvent) => {
    setServerError('')
    setChangingFile({fileChange: true, changeMessage: translationsMessages.loadingFile})
    const files: FileList = (event?.target as HTMLInputElement)?.files

    if(!checkFileProperties(files)) {
      // @ts-ignore
      event.target.value = ''
    }
    if(scanForVirusInfo?.scanForVirus || pathname.includes('onboarding')){
      // all files on onboarding have to go to the cloudmersive api by default unless otherwise specified
      const virusScanUrlValid = scanForVirusInfo?.scanForVirus
        ? (scanForVirusInfo?.virusScanUrl || '')
        : (pathname.includes('onboarding') && 'AccountOpeningForms/virus_scan_file')
      if(!virusScanUrlValid) return isLoading
      checkForVirus(virusScanUrlValid, files, event)
      return
    }
    setEventFileList(event)
    const filePayload = !labelAsTextInput
      ? { name: files?.[0]?.name || '', data: files?.[0], required: required }
      : { name: files?.[0]?.name || '', data: files?.[0], required: required, inputTextName: (document.getElementById(labelTextId) as HTMLInputElement)?.value }

    props?.onChange?.(
      filePayload,
      data => {
        setFileData({ name: files?.[0]?.name || '', data: files?.[0], documentID: data})
        setChangingFile({fileChange: false, changeMessage: ''})
      },
      downloadOnlyFileInfo?.downloadFileID
    )
  }

  const removeFile = () => {
    setServerError('')
    eventFileList && setEventFileList(eventFileInfo => {
      const temp = {...eventFileInfo}
      // @ts-ignore
      temp.target.value = ''
      return temp
    })

    fileData?.name && setChangingFile({fileChange: true, changeMessage: translationsMessages.removingFile})

    deleteFile(fileData?.documentID, () => {
      setFileData({deleted: true})
      customOnDelete?.(fileData)
      setChangingFile({fileChange: false, changeMessage: ''})
      customOnChange?.({value: {...fileData, removeFile: true}, name})
    })
  }

  const resetClick = async (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    removeFile()
  }

  const uploadKeyDown = (event: KeyboardEvent) => {
    if(event.key === 'Enter') {
      (document.querySelector(`#${fileInputIdentifier}`) as HTMLInputElement)?.click()
    }
  }

  const resetKeyDown = async (event: KeyboardEvent) => {
    if(event.key === 'Enter') {
      removeFile()
    }
  }

  useEffect(() => {
    if(error) {
      setChangingFile({fileChange: false, changeMessage: ''})
      setServerError('Server error')
    }
  },[error])

  useEffect(() => {
    if (fileData || serverFileName?.name === '' || fileData?.deleted) return

    setFileData({ name: serverFileName?.name, documentID: serverFileName?.documentID, data: null })
  }, [serverFileName])

  const fileInputText = serverError || fileData?.name  || <MaxSizeSpan>{translationsMessages.fileSize}</MaxSizeSpan>

  const fieldChangeColor = changingFile?.fileChange ? 'main' : 'ultraViolet'

  return (
    <LabelWrapper
      disabled={disabled}
      removeMargin={props.removeMargin}
      width={props.width}
      margin={props.margin}
      wrapperPadding={props.wrapperPadding}
      wrapperBoxSizing={props.wrapperBoxSizing}
    >
      <InputStyle
        type='file'
        name={name}
        id={fileInputIdentifier}
        disabled={disabled}
        accept={accept}
        multiple={props.multiple}
        onChange={onChange}
        onClick={e => {
          if(labelAsTextInput && !(document.getElementById(labelTextId) as HTMLInputElement)?.value) {
            setInsertNameInfo(true)
            e.preventDefault()
          }
        }}
      />
      <MainWrapper
        justifyContent='space-between'
        alignItems={labelAsTextInputLabel ? 'flex-end': 'center'}
        error={error}
        labelAsTextInput={labelAsTextInput}
        removeBorder={removeBorder}
      >
        {label && !labelAsTextInput && (
          <LabelBlock>
            <Font bold>{label}</Font>
          </LabelBlock>
        )}
        {!label && labelAsTextInput && (
          <InputLabelWrapper labelAsTextInput={labelAsTextInput}>
            <label htmlFor={labelTextId}>
              {labelAsTextInputLabel}<RequiredMark>*</RequiredMark>
              {
                insertNameInfo &&
                    <Font color='error'>{t('components.inputFiles.insertNameFirst')}</Font>
              }
            </label>
            <InputStyle
              onChange={() => insertNameInfo === true && setInsertNameInfo(false)}
              labelAsTextInput
              placeholder={labelAsTextInputPlaceholder}
              name={labelTextId}
              id={labelTextId}
              defaultValue={labelAsTextInputServerValue || ''}
            />
          </InputLabelWrapper>
        )}
        {
          downloadOnlyFileInfo?.name !== undefined &&
          <InputLabelWrapper>
            <NameWrapper as='label' justifyContent='space-between' gap='16px'>
              <Font bold color={error ? 'error' : 'ultraViolet'}>{downloadOnlyFileInfo?.name || error || ''}</Font>
              <Flex alignItems='flex-end' onClick={() => {
                downloadFile(downloadOnlyFileInfo?.downloadFileID, downloadOnlyFileInfo.name)
              }}>
                <Icon aria-label='upload icon' name='download' size='s' color='ultraViolet' keyDownFunction={uploadKeyDown}/>
              </Flex>
            </NameWrapper>
          </InputLabelWrapper>
        }
        <InputLabelWrapper
          labelAsTextInput={labelAsTextInput}
          label={label}
          onClick={() => {
            if(serverFileName?.documentID && serverFileName?.name && (isDocumentation || isEmployee)) {
              downloadPreviewClick(serverFileName?.documentID, serverFileName?.name, true)
              return
            }
            if((!isDocumentation && isEmployee) && fileData) {
              downloadPreviewClick(fileData?.documentID, fileData?.data?.name, true)
              return
            }
          }}
          isDocumentation={isDocumentation || isEmployee}
        >
          {labelAsTextInput && <label>{labelForInputFile}</label>}
          <NameWrapper
            as='label'
            htmlFor={(isEmployee && fileData?.name) ? undefined : fileInputIdentifier}
            justifyContent='space-between'
            gap='16px'
            label={label}
            labelAsTextInput={labelAsTextInput}
          >
            <Font bold color={serverError ? 'error' : fieldChangeColor}>
              {
                changingFile?.fileChange
                  ? changingFile?.changeMessage
                  : fileInputText
              }
            </Font>
            <Flex alignItems='flex-end'>
              {fileData?.name && !error && <Icon name='check' size='s' color='success' />}
              <Icon aria-label='upload icon' name='upload' size='s' color='ultraViolet' keyDownFunction={uploadKeyDown} />
              <Icon name='close' size='s' color='error' clickFunction={resetClick} keyDownFunction={resetKeyDown}/>
            </Flex>
          </NameWrapper>
        </InputLabelWrapper>
        {
          !singleFileInput &&
          !removeRemoveButton &&
          <Button
            overflow='none'
            onClick={() => deleteRow(fileInputId, fileData.documentID)}
            label='Remove'
          />
        }
      </MainWrapper>
      {
        fileError.error &&
        <Popup
          removeSelfClose
          label={fileError.message}
          type='error'
          popupCloseHelperFunction={() => setFileError({error: false, message: ''})}
          click={() => setFileError({error: false, message: ''})}
        />
      }
    </LabelWrapper>
  )
}

InputFiles.defaultProps = {
  required: false,
  disabled: false,
  removeMargin: false,
  multiple: false,
  accept: '',
  label: '',
  error: '',
  labelAsTextInput: false,
}

export default InputFiles
